<template>
  <div>
    <h2 class="form-section-header">Add Contacts</h2>

    <div class="inline-form-group">
      <el-col :span="11">
        <el-input
          v-model="form.FIRSTNAME"
          type="text"
          placeholder="Enter first name"
        >
          <template slot="prefix">First name</template>
        </el-input>
      </el-col>
      <el-col :span="1"></el-col>
      <el-col :span="12">
        <el-input
          v-model="form.LASTNAME"
          type="text"
          placeholder="Enter last name"
        >
          <template slot="prefix">Last name</template>
        </el-input>
      </el-col>
    </div>
    <div class="inline-form-group">
      <el-col :span="11">
        <div class="el-input el-input--prefix">
          <input
            v-model="form.PHONE1"
            type="text"
            autocomplete="off"
            placeholder="Enter phone number"
            class="el-input__inner"
          />
          <span class="el-input__prefix">Phone</span>
        </div>
      </el-col>
      <el-col :span="1"></el-col>
      <el-col :span="12">
        <el-input v-model="form.EMAIL" type="text" placeholder="Enter email">
          <template slot="prefix">Email</template>
        </el-input>
      </el-col>
    </div>
    <div class="inline-form-group">
      <el-col :span="11">
        <el-input
          v-model="form.COMPANY"
          type="text"
          placeholder="Enter company"
        >
          <template slot="prefix">Company</template>
        </el-input>
      </el-col>
      <el-col :span="5"></el-col>
      <el-col v-if="fieldsValid" :span="8" style="text-align: right">
        <el-tooltip content="Add" placement="top">
          <el-button circle type="primary" @click="addContact">
            <el-icon class="el-icon-plus"></el-icon>
          </el-button>
        </el-tooltip>
        <el-tooltip content="Clear" placement="top">
          <el-button circle type="danger" @click="clearFields">
            <el-icon class="el-icon-delete"></el-icon>
          </el-button>
        </el-tooltip>
      </el-col>
    </div>

    <div id="contact-summary-block">
      <h3 class="form-section-header">And / Or</h3>
      <div class="inline-form-group align-center" style="margin-top: 10px">
        <bulk-example :file-path="bulkFileExample">
          <el-button slot="hoverRef" type="primary" @click="startImport">
            Import from csv
          </el-button>
        </bulk-example>
        <el-button
          type="danger"
          class="ml-5"
          icon="el-icon-close"
          v-if="fileSelected"
          @click="closeImporting"
        >
        </el-button>
        <el-button
          type="primary"
          class="ml-5"
          icon="el-icon-check"
          v-if="csvReady"
          @click="addToBulk"
        >
        </el-button>
      </div>
      <div class="csv-import" v-show="importVisible">
        <upload-csv
          ref="fileButton"
          v-model="parsedCSV"
          :map-fields="[
            { label: 'First Name', key: 'FIRSTNAME' },
            { label: 'Last Name', key: 'LASTNAME' },
            { label: 'Phone', key: 'PHONE1' },
            { label: 'Email', key: 'EMAIL' },
            { label: 'Company', key: 'COMPANY' },
          ]"
          @input="csvReady = true"
          @fileSelected="submitForMapping"
          :headers="true"
        >
        </upload-csv>
      </div>
    </div>

    <div v-if="bulkContacts.length" class="contacts-table">
      <div class="flex align-center">
        <div class="box">
          <el-input v-model="search" placeholder="Search" clearable />
        </div>

        <div class="box right">
          <el-tag>
            <i class="el-icon-info" style="margin-right: 4px"></i>
            Total: {{ contactsCount }}
          </el-tag>
        </div>
      </div>

      <el-table
        :data="filteredContacts"
        class="modal-summary-table mb-10"
        height="250px"
      >
        <el-table-column prop="FIRSTNAME" label="First Name"> </el-table-column>
        <el-table-column prop="LASTNAME" label="Last Name"> </el-table-column>
        <el-table-column
          prop="PHONE1"
          label="Number"
          :formatter="numberFormatter"
        >
        </el-table-column>
        <el-table-column>
          <template slot-scope="scope">
            <el-button
              class="table-action-button"
              @click="removeFromBulk(scope.$index)"
              type="default"
              size="small"
              icon="el-icon-minus"
            >
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import UploadCSV from "@/components/upload-csv";
import BulkExample from "@/components/bulkExample";

import { debounce } from "lodash";
import {phoneNumberFormatter} from "@/globals/globals";

export default {
  name: "PhonebooksContactForm",

  props: {
    context: { required: true },
    contacts: { required: true },
    clearForm: { required: true },
  },

  components: {
    UploadCsv: UploadCSV,
    BulkExample,
  },

  data() {
    return {
      bulkFileExample:
        "https://global-app.s3.amazonaws.com/omnia-assets/bulk_contacts_example.csv",
      bulkContacts: [],
      fieldsValid: false,
      parsedCSV: null,
      importVisible: false,
      csvReady: false,
      fileSelected: false,

      form: {
        FIRSTNAME: "",
        LASTNAME: "",
        PHONE1: "",
        EMAIL: "",
        COMPANY: "",
      },

      search: "",
      query: "",
    };
  },

  computed: {
    fieldLength() {
      let concat = "";
      Object.keys(this.form).forEach((key) => {
        concat += this.form[key];
      });
      return concat.length;
    },

    filteredContacts() {
      return this.bulkContacts.filter(
        (data) =>
          !this.query ||
          data.FIRSTNAME.toLowerCase().includes(this.query.toLowerCase()) ||
          data.LASTNAME.toLowerCase().includes(this.query.toLowerCase()) ||
          data.PHONE1.toLowerCase().includes(this.query.toLowerCase())
      );
    },

    contactsCount() {
      return this.filteredContacts.length;
    },
  },

  watch: {
    fieldLength() {
      if (this.fieldLength) {
        this.fieldsValid = true;
      } else {
        this.fieldsValid = false;
      }
    },

    contacts(contacts) {
      if (contacts && contacts.length) {
        this.bulkContacts = [...contacts];
      }
    },

    clearForm(val) {
      if (val) {
        this.clearFields();
      }
    },

    search: debounce(function (query) {
      this.query = query;
    }, 500),
  },

  methods: {
    emitContacts() {
      this.$emit("change", this.bulkContacts);
    },

    handleRemove() {
      this.clearFields();
    },

    addContact() {
      const contact = { ...this.form };
      contact.PHONE1 = contact.PHONE1.replace(/(-|\(|\)|\s)/g, "");

      this.bulkContacts.push(contact);
      this.clearFields(false);

      // ⭐️ Emit contacts ⭐️ //
      this.emitContacts();
    },

    clearFields(bulk = true) {
      Object.keys(this.form).forEach((key) => {
        this.form[key] = "";
      });
      this.fieldsValid = false;
      this.search = "";
      this.query = "";

      if (bulk) {
        this.bulkContacts = [];
      }
    },

    numberFormatter(row) {
      return phoneNumberFormatter(row.PHONE1);
    },

    closeImporting() {
      this.parsedCSV = null;
      this.csvReady = false;
      this.fileSelected = false;
      this.importVisible = false;
      this.$refs.fileButton.sample = null;
      this.$refs.fileButton.fileSelected = false;
      this.$refs.fileButton.map = {};
      this.$refs.fileButton.isValidFileMimeType = false;
      this.$refs.fileButton.form = { csv: null };
      let fileButton = document.querySelector(".form-control-file");
      fileButton.value = null;
    },

    addToBulk() {
      const values = [...this.parsedCSV];
      for (let i = 0; i < values.length; i++) {
        this.bulkContacts.push(values[i]);
      }
      this.closeImporting();

      // ⭐️ Emit contacts ⭐️ //
      this.emitContacts();
    },

    startImport() {
      this.importVisible = true;
      const fileButton = document.querySelector(".form-control-file");
      fileButton.click();
    },

    removeFromBulk(index) {
      this.bulkContacts.splice(index, 1);

      // ⭐️ Emit contacts ⭐️ //
      this.emitContacts();
    },

    submitForMapping() {
      this.fileSelected = true;
      this.$refs.fileButton.load();
    },
  },
};
</script>

<style lang="scss" scoped>
#contact-summary-block {
  max-width: 600px;
  margin: 15px auto;
}

.contacts-table {
  max-width: 600px;
  margin: 0 auto;
}

.modal-summary-table {
  max-width: 600px;
  margin: 20px auto;
}

.vue-csv-uploader {
  input[type="file"] {
    display: none;
  }
}
</style>
